import Konva from "konva";
import { Shape } from "react-konva";
import { Frame } from "shared/datamodel/schemas/frame";

export default function TitleTextInput({ id, element }: { id: string; element: Frame }) {
  const { x, y, width, title, scaleX = 1 } = element;
  return (
    <Shape
      x={x}
      y={y}
      sceneFunc={(context: Konva.Context, shape: Konva.Shape) => {
        const stage = shape.getStage();
        if (!stage) return;
        let scale = 1;
        if ((context as any).isPdfContext) {
          scale = (context as any).pageScale;
        } else {
          scale = stage.scaleX();
        }
        let scaledFontSize = 12 / Math.min(scale, 1);

        context._context.font = `300 ${scaledFontSize}px Poppins`;
        context._context.fillStyle = "#797E93";
        context.beginPath();

        // Clear any cached text when title changes
        if (shape.attrs.cachedTitle !== title) {
          shape.attrs.displayText = null;
          shape.attrs.cachedTitle = title;
        }

        let displayText = "";
        const roundedFontSize = Math.round(scaledFontSize);
        const maxWidth = width * scaleX;
        if (
          shape.attrs.displayText &&
          shape.attrs.scaledFontSize === roundedFontSize &&
          shape.attrs.maxWidth === maxWidth
        ) {
          displayText = shape.attrs.displayText;
        } else {
          // Measure the width of the full text
          const textWidth = context._context.measureText(title).width;

          // If text width exceeds maxWidth, truncate and add ellipsis
          if (textWidth > maxWidth) {
            const truncatedText = title;
            const length = truncatedText.length;

            // Binary search to find the maximum substring that fits
            let start = 0;
            let end = length;
            let fitText = "";
            const ellipsis = "...";

            while (start < end) {
              const mid = Math.ceil((start + end) / 2);
              const testText = truncatedText.slice(0, mid);
              const testWidth = context._context.measureText(testText + ellipsis).width;

              if (testWidth <= maxWidth) {
                start = mid;
                fitText = testText;
              } else {
                end = mid - 1;
              }
            }

            // Ensure the fitText actually fits
            if (context._context.measureText(fitText + ellipsis).width > maxWidth) {
              fitText = fitText.slice(0, -1);
            }
            if (fitText.length === 0) {
              displayText = "";
            } else {
              displayText = fitText + ellipsis;
            }
          } else {
            // Draw the full text
            displayText = title;
          }

          // set in cache
          shape.attrs.scaledFontSize = roundedFontSize;
          shape.attrs.displayText = displayText;
          shape.attrs.maxWidth = maxWidth;
        }

        context.fillText(displayText, 0, -10 / Math.min(scale, 1));
      }}
      hitFunc={(context, shape) => {
        // Customize the hit area, e.g., making it match the width of the text and height of the font
        const stage = shape.getStage();
        if (!stage) return;
        const scale = stage.scaleX();
        const scaledFontSize = 12 / Math.min(scale, 1);
        const maxWidth = width * scaleX;
        const additionalHeight = 12 / Math.min(scale, 1); // Add extra height
        const yPosition = -scaledFontSize - additionalHeight; // Move hit area higher
        const height = scaledFontSize + additionalHeight; // Expand hit area

        // Define the rectangular hit area
        context.beginPath();
        context.rect(0, yPosition, maxWidth, height);
        context.closePath();
        context.fillStrokeShape(shape);
      }}
      id={id}
      isCanvasElement={true}
      isFrameTitle={true}
      isDraggable={true}
      isFrame={true}
      isConnector={false}
      isConnectable={false}
      isSelectable={true}
      element={element}
    />
  );
}
